import React, { useState, useEffect, useRef, useContext } from 'react';
import { flushSync } from 'react-dom';
import { NetworkUserCard } from './NetworkUserCard';
import { IoChevronBackOutline, IoChevronForwardOutline } from 'react-icons/io5';

// Use a shared context so any node can command the top-level scroll view to pan
export const ScrollContext = React.createContext(null);

export const NetworkTreeNode = ({ user, isRoot = false, onShowDetails }) => {
  const [isExpanded, setIsExpanded] = useState(user.autoExpand || false);
  const [visibleCount, setVisibleCount] = useState(5);

  useEffect(() => {
    if (user.autoExpand) {
      setIsExpanded(true);
    }
  }, [user]);

  const containerRef = useContext(ScrollContext);
  const nodeRef = useRef(null);

  const hasChildren = user.children && user.children.length > 0;
  const totalChildren = hasChildren ? user.children.length : 0;
  const visibleChildren = hasChildren 
     ? user.children.slice(0, visibleCount) 
     : [];

  const anchorScroll = (callback) => {
    if (!containerRef?.current || !nodeRef.current) {
       callback();
       return;
    }
    const beforeRect = nodeRef.current.getBoundingClientRect();
    
    flushSync(() => {
       callback();
    });
    
    if (!nodeRef.current) return;
    const afterRect = nodeRef.current.getBoundingClientRect();
    
    const shiftX = afterRect.left - beforeRect.left;
    const shiftY = afterRect.top - beforeRect.top;
    
    if (shiftX !== 0 || shiftY !== 0) {
      containerRef.current.scrollLeft += shiftX;
      containerRef.current.scrollTop += shiftY;
    }
  };

  const handleToggle = () => anchorScroll(() => setIsExpanded(p => !p));
  const handleShowLess = () => anchorScroll(() => setVisibleCount(c => Math.max(5, c - 5)));
  const handleShowMore = () => anchorScroll(() => setVisibleCount(c => Math.min(totalChildren, c + 5)));

  // Build unified inline items array for rendering in the child row
  const displayItems = [];
  if (hasChildren && isExpanded) {
    if (visibleCount > 5) {
      displayItems.push({
        type: 'prev',
        id: 'btn-prev',
        onClick: handleShowLess
      });
    }
    visibleChildren.forEach((child) => {
      displayItems.push({
        type: 'child',
        id: child.id,
        data: child
      });
    });
    if (visibleCount < totalChildren) {
      displayItems.push({
        type: 'next',
        id: 'btn-next',
        onClick: handleShowMore,
        remaining: totalChildren - visibleCount
      });
    }
  }

  return (
    <div className="tree-node-wrapper">
      <div ref={nodeRef} className="tree-node-anchor">
        <NetworkUserCard 
          user={user} 
          isExpanded={isExpanded} 
          onToggle={handleToggle} 
          onShowDetails={onShowDetails}
          isRoot={isRoot}
        />
        {/* Parent vertical drop line */}
        {hasChildren && isExpanded && (
          <div className="vertical-connector-primary" />
        )}
      </div>

      {hasChildren && isExpanded && (
        <div className="children-row-container">
          <div className="children-list-flex">
            {displayItems.map((item, idx) => {
              const isFirst = idx === 0;
              const isLast = idx === displayItems.length - 1;
              return (
                <div key={item.id} className="child-node-slot">
                  {/* Continuous Horizontal Connector Lines */}
                  {displayItems.length > 1 && (
                    <>
                      {!isFirst && <div className="horizontal-connector-left" />}
                      {!isLast && <div className="horizontal-connector-right" />}
                    </>
                  )}
                  {/* Vertical Connector Line dropping to the child/card */}
                  <div className="vertical-connector-child" />
                  
                  {item.type === 'child' && (
                    <NetworkTreeNode user={item.data} onShowDetails={onShowDetails} />
                  )}

                  {item.type === 'prev' && (
                    <div 
                      onClick={(e) => { e.stopPropagation(); item.onClick(); }} 
                      className="user-card-glass pagination-card prev-card interactive"
                      title="Show Less"
                    >
                      <div className="pagination-card-body">
                        <div className="pagination-icon-circle">
                          <IoChevronBackOutline size={20} />
                        </div>
                        <span className="pagination-text-primary">Show Less</span>
                        <span className="pagination-text-secondary">Collapse 5</span>
                      </div>
                    </div>
                  )}

                  {item.type === 'next' && (
                    <div 
                      onClick={(e) => { e.stopPropagation(); item.onClick(); }} 
                      className="user-card-glass pagination-card next-card interactive"
                      title={`Show ${item.remaining} More`}
                    >
                      <div className="pagination-card-body">
                        <div className="pagination-icon-circle">
                          <IoChevronForwardOutline size={20} />
                        </div>
                        <span className="pagination-text-primary">Show More</span>
                        <span className="pagination-text-secondary">+{item.remaining} Nodes</span>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
